import React from "react";
import { useTranslation } from "react-i18next";
import { Settings } from "lucide-react";
import BasicAdvancedWrapper, {
  BasicAdvancedWrapperProps,
} from "../components/BasicAdvancedWrapper";
import { MultiStepConfig } from "../components/MultiStepGradeModal";
import { CreateModalProps } from "../index";
import { ExitTicketAdvancedStep } from "./steps";
import { ExitTicketGeneratorCreateRequestDto } from "@packages/api/madlenAPI.schemas";

interface ExitTicketCreateModalProps
  extends Omit<CreateModalProps, "onSubmit"> {
  onSubmit: (input: ExitTicketGeneratorCreateRequestDto) => Promise<void>;
}

export const ExitTicketCreateModal: React.FC<ExitTicketCreateModalProps> = ({
  onSubmit,
  initialInput,
  ...props
}) => {
  const { t } = useTranslation();

  // Define the initial data structure for advanced mode
  const initialAdvancedData: ExitTicketGeneratorCreateRequestDto = {
    // Basic CreateModal data (populated from first step)
    courseName: initialInput.courseName as any,
    gradeLevel: initialInput.gradeLevel,
    prompt: initialInput.prompt,
    environment: initialInput.environment,
    isPublic: initialInput.isPublic,
    generationType: initialInput.generationType as any,
    teacherDocumentIds: initialInput.teacherDocumentIds || [],
    learningOutcomeIds: initialInput.learningOutcomeIds || [],
    contentLanguage: initialInput.outputLanguage as any,

    // Advanced exit ticket-specific defaults
    complexity: "QUICK",
    purpose: "INSTRUCTIONAL_FEEDBACK",
  };

  // Define the advanced steps configuration
  const advancedSteps: MultiStepConfig<ExitTicketGeneratorCreateRequestDto>[] =
    [
      {
        id: "options-settings",
        title: t("exitTickets.steps.options.title", "Seçenekler ve Ayarlar"),
        description: t(
          "exitTickets.steps.options.description",
          "Çıkış biletinizin karmaşıklığını ve amacını seçin",
        ),
        icon: <Settings size={16} />,
        component: ExitTicketAdvancedStep,
        validation: (data) => {
          // Complexity is required
          if (!data.complexity) return false;

          // Purpose is required
          if (!data.purpose) return false;

          // If purpose is custom (not one of the predefined options), it needs to have content
          if (
            data.purpose !== "INSTRUCTIONAL_FEEDBACK" &&
            data.purpose !== "AFFECTIVE_REFLECTION" &&
            !data.purpose.trim()
          ) {
            return false;
          }

          return true;
        },
      },
    ];

  // Handle basic mode submission (original behavior)
  const handleBasicSubmit = async (data: any) => {
    // Add default values for advanced-only fields
    const submissionData = {
      ...data,
      complexity: "QUICK", // Default complexity for basic mode
      purpose: "INSTRUCTIONAL_FEEDBACK", // Default purpose for basic mode
    };

    await onSubmit(submissionData);
  };

  // Handle advanced mode submission
  const handleAdvancedSubmit = async (
    data: ExitTicketGeneratorCreateRequestDto,
  ) => {
    await onSubmit(data);
  };

  // Prepare the wrapper props
  const wrapperProps: BasicAdvancedWrapperProps<ExitTicketGeneratorCreateRequestDto> =
    {
      createModalProps: {
        ...props,
        initialInput,
        themeColor: "mint",
        onSubmit: handleBasicSubmit, // This will be set by the wrapper
        // No specific fields needed for basic mode
      },
      multiStepProps: {
        steps: advancedSteps,
        initialData: initialAdvancedData,
        title: t("quickActions.exitTicketsCreate", "Create Exit Ticket"),
        themeColor: "mint",
        backPath: "/content/exit-tickets",
        isLoading: props.isLoading,
        error: props.error,
        loadingTitle: props.loadingTitle,
        errorTitle: props.errorTitle,
        errorDescription: props.errorDescription,
        onFinalSubmit: handleAdvancedSubmit,
        basicModeSubmitText: t("common.generate", "Generate"),
        advancedModeSubmitText: t(
          "exitTickets.createAdvanced",
          "Create Advanced Exit Ticket",
        ),
      },
      themeColor: "mint",
      title: t("quickActions.exitTicketsCreate", "Create Exit Ticket"),
      backPath: "/content/exit-tickets",
      enableAdvancedMode: true,
      defaultMode: "basic",
      showModeToggle: true,
      onBasicSubmit: handleBasicSubmit,
      onAdvancedSubmit: handleAdvancedSubmit,
    };

  return <BasicAdvancedWrapper {...wrapperProps} />;
};

export default ExitTicketCreateModal;
